import {
	ArrowLeft,
	ArrowRightLeft,
	Clipboard,
	Copy,
	Link as LinkIcon,
	Plus,
	Unlink,
	ZoomIn,
} from 'lucide-react';
import { DamageIcon } from '@/components/entity-icons';
import { tabIntoForm } from '@/lib/utils/focus';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { toast } from 'sonner';

import type { DamageType } from '@/common/damage';
import { ROLE } from '@/common/role';
import { DeleteConfirmDialog } from '@/components/soft-delete/DeleteConfirmDialog';
import { DeletedBadge } from '@/components/soft-delete/DeletedInfo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDamageMutations } from '@/hooks/use-damage-mutations';
import { useAssetReadOnly } from '@/context/AssetReadOnlyContext';
import { usePermission } from '@/hooks/use-permission';
import { usePolygonMutations } from '@/hooks/use-polygon-mutations';
import { useSoftDeleteItem } from '@/hooks/use-soft-delete';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';

import { DataUploadListContainer } from '../data-upload/data-upload-list/page';
import { Restricted } from '../Restricted';
import { DamageForm } from './damage-form';
import { MoveDamageDialog } from './move-damage-dialog';

type DamageOverviewProps = {
	damage: DamageType | null;
	isNew?: boolean;
	assetId: string;
	inspectionId: string;
	onBack: () => void;
	/**
	 * A deleted damage opened as a read-only preview (GH #331): the banner goes above the tabs, and only the
	 * overview is shown (its files were deleted with it).
	 */
	deletedBanner?: React.ReactNode;
	/** Restore of a deleted item previewed read-only (GH #331), where Delete sits on a live one */
	restoreButton?: React.ReactNode;
};

export function DamageOverview({
	damage,
	isNew,
	assetId,
	inspectionId,
	onBack,
	deletedBanner,
	restoreButton,
}: DamageOverviewProps) {
	const { t } = useTranslation();
	const [activeTab, setActiveTab] = useState('overview');
	const navigate = useNavigate();

	const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

	const [title, setTitle] = useState(damage?.title || (isNew ? t('damages.detail.newTitle') : ''));
	const titleInputRef = useRef<HTMLInputElement>(null);

	// A new damage starts with the cursor in its title
	useEffect(() => {
		if (isNew) titleInputRef.current?.select();
	}, [isNew]);

	const shouldReadDataUploads = usePermission(ROLE.DATA_UPLOADS_READ);
	const shouldUpdateDamages = usePermission(ROLE.DAMAGES_UPDATE);
	const { isReadOnly } = useAssetReadOnly();

	const { createdPolygons, mapActions, activeDamage, calculateDamageSize } = useOutletContext<{
		createdPolygons: any[];
		mapActions: (action: { type: string; payload: any }) => void;
		activeDamage: any;
		calculateDamageSize: (damageId: string, measurementType: 'area' | 'perimeter') => any;
	}>();

	// Callback after damage is successfully created
	const onDamageCreated = async (newDamage?: DamageType) => {
		activeDamage({
			damageId: newDamage?.id,
			hasPolygon: newDamage?.hasPolygons,
		});
		mapActions({
			type: 'save',
			payload: { damageId: newDamage?.id },
		});

		// If this was a NEW damage creation AND we have polygons waiting
		if (isNew && newDamage?.id && createdPolygons && createdPolygons.length > 0) {
			try {
				await createPolygonMutation.mutateAsync({
					assetId,
					inspectionId,
					damageId: newDamage.id,
					polygon: {
						type: 'Polygon',
						coordinates: createdPolygons,
					},
				});
				toast.success(t('damages.toast.polygonLinked'));
			} catch (error) {
				toast.error(t('damages.toast.polygonLinkFailed'));
			}
		}
	};

	const { createMutation: createPolygonMutation } = usePolygonMutations({
		assetId,
		inspectionId,
		damageId: damage?.id || 'new',
	});

	const { createMutation, updateMutation, isSubmitting } = useDamageMutations({
		assetId,
		inspectionId,
		damageId: damage?.id,
		onCreateSuccess: onDamageCreated,
		onUpdateSuccess: (newDamage) => {
			mapActions({ type: 'save', payload: { damageId: newDamage?.id } });
		},
	});

	useEffect(() => {
		if (damage && !isNew) {
			activeDamage({ damageId: damage?.id, hasPolygon: damage?.hasPolygons });
			if (damage.title) setTitle(damage.title);
		}
	}, [damage, isNew]);

	// --- Handlers ---

	const handleDuplicate = () => {
		if (!damage) return;
		const { id, hasPolygons, ...rest } = damage;
		const newTitle = damage.title
			? t('damages.detail.copyOfTitle', { title: damage.title })
			: t('damages.detail.copyOfDamage');
		const duplicatedData = { ...rest, title: newTitle, hasPolygons: false };

		navigate(`/assets/${assetId}/inspections/${inspectionId}/damages/new`, {
			state: { duplicatedDamage: duplicatedData },
		});
	};

	const handleFormSubmit = (formData: any, shouldClose: boolean) => {
		const payload = { ...formData, title };
		const mutationOptions = {
			onSuccess: (newDamage: DamageType | null) => {
				if (shouldClose) {
					onBack();
				} else {
					toast.success(isNew ? t('damages.toast.createSuccess') : t('damages.toast.saveSuccess'));
					if (isNew) {
						navigate(`/assets/${assetId}/inspections/${inspectionId}/damages/${newDamage?.id}`);
					}
				}
			},
		};

		if (isNew) {
			createMutation.mutate(payload, mutationOptions);
		} else if (damage?.id) {
			updateMutation.mutate(payload, mutationOptions);
		}
	};

	// Delete with cascade (GH #331): the dialog shows what goes with the damage, or the work packages
	// that block it; the toast offers Undo
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const softDeleteDamage = useSoftDeleteItem({
		onDeleted: () => {
			setIsDeleteOpen(false);
			onBack();
			if (damage?.id) mapActions({ type: 'delete', payload: { damageId: damage.id } });
		},
	});
	const handleDelete = () => {
		if (damage?.id) {
			setIsDeleteOpen(true);
		}
	};
	const damageName = damage?.title || t('common.entities.damage', { count: 1 });

	// --- Display Helpers ---
	const displayId = damage?.id || (isNew ? 'New-ID' : '');
	const has3DLink = damage?.hasPolygons;

	const copyToClipboard = async (text: string, msg: string) => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success(msg);
		} catch {
			toast.error(t('damages.toast.copyFailed'));
		}
	};

	const tabList = [
		{ value: 'overview', label: t('damages.detail.tabs.overview'), disabled: false },
		{
			value: 'data-upload',
			label: t('damages.detail.tabs.dataUpload'),
			disabled: isNew,
			hidden: !shouldReadDataUploads,
		},
	].filter((tab) => !deletedBanner || tab.value === 'overview');

	const handleHighlightDamage = (damageId: string, hasPolygon: boolean, e: React.MouseEvent) => {
		e.stopPropagation();
		if (!hasPolygon) return;
		mapActions({ type: 'zoom', payload: { damageId } });
	};

	return (
		<>
			<div className="border-b text-card-foreground bg-card flex-none">
				<div className="p-6 pb-4">
					<Button
						variant="ghost"
						className="text-muted-foreground hover:text-primary hover:bg-transparent! px-0! mb-2 gap-0 hover:pl-2! cursor-pointer"
						onClick={onBack}
					>
						<ArrowLeft className="size-4 mr-1!" /> {t('damages.detail.backToList')}
					</Button>

					<div className="flex justify-between items-start mb-4 gap-4">
						<div className="flex items-center gap-2 flex-1 min-w-0">
							<DamageIcon
								className="size-5 md:size-6 shrink-0 text-muted-foreground"
								aria-label={t('common.entities.damage', { count: 1 })}
							/>
							<Input
								ref={titleInputRef}
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								onKeyDown={tabIntoForm}
								className="border-transparent hover:border-foreground/20 dark:bg-transparent text-lg md:text-2xl font-bold px-2 py-1 h-auto w-full shadow-none"
								placeholder={t('damages.detail.titlePlaceholder')}
								disabled={!shouldUpdateDamages}
							/>
							{/* A deleted damage previewed read-only: tagged next to its title, like a deleted inspection */}
							{deletedBanner && <DeletedBadge />}
						</div>
						<div className="shrink-0 pt-2">
							{has3DLink ? (
								<div className="flex items-center gap-1.5 bg-muted dark:bg-primary/20 border border-primary/20 text-primary px-2 py-1 rounded-md text-xs font-medium shadow-sm">
									<button
										type="button"
										onClick={(e) => handleHighlightDamage(damage?.id!, true, e)}
										className="hover:text-primary/80 hover:bg-primary/20 p-0.5 rounded transition-colors"
										title={t('damages.detail.viewOnMap')}
									>
										<ZoomIn className="size-4" />
									</button>
									<span className="select-none">{t('damages.detail.linked')}</span>
									{!isReadOnly && (
										<>
											<div className="w-px h-3 bg-primary/20 mx-0.5" />
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													if (confirm(t('damages.detail.unlinkConfirm'))) {
														mapActions({
															type: 'unlink',
															payload: { damageId: damage?.id },
														});
													}
												}}
												className="text-primary hover:text-destructive hover:bg-primary/20 p-0.5 rounded transition-colors"
												title={t('damages.detail.removeLink')}
											>
												<Unlink className="h-3.5 w-3.5" />
											</button>
										</>
									)}
								</div>
							) : (
								<div className="flex items-center gap-1.5 bg-muted/50 border border-border text-muted-foreground px-2 py-1 rounded-md text-xs font-medium shadow-sm">
									<span className="select-none opacity-70">{t('damages.detail.notLinked')}</span>
									{!isNew && !isReadOnly && <div className="w-px h-3 bg-border mx-0.5" />}
									{!isNew && !isReadOnly && (
										<button
											type="button"
											onClick={() => toast.info(t('damages.toast.markDamageHint'))}
											className="text-primary hover:text-primary/80 hover:bg-muted p-0.5 rounded transition-colors"
											title={t('damages.detail.addLink')}
										>
											<Plus className="h-3.5 w-3.5" />
										</button>
									)}
								</div>
							)}
						</div>
					</div>

					<div className="flex items-center gap-3 px-2 mb-4">
						<div className="flex items-center gap-1">
							<code className="text-sm text-muted-foreground font-mono bg-muted/50 px-1.5 py-0.5 rounded">
								{displayId}
							</code>
							{!isNew && (
								<>
									<ActionIcon
										icon={Clipboard}
										onClick={() => copyToClipboard(displayId, t('damages.toast.idCopied'))}
										title={t('damages.detail.copyId')}
									/>
									<ActionIcon
										icon={LinkIcon}
										onClick={() => {
											const damageUrl = `${window.location.origin}${window.location.pathname}#damage/${damage?.id}`;
											copyToClipboard(damageUrl, t('damages.toast.linkCopied'));
										}}
										title={t('damages.detail.copyLink')}
									/>
								</>
							)}
						</div>
					</div>
				</div>

				{deletedBanner}
				<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-0">
					<TabsList className="w-full justify-start rounded-none border-b border-transparent bg-transparent p-0">
						{tabList.map((tab) => (
							<TabsTrigger
								key={tab.value}
								value={tab.value}
								disabled={tab.disabled}
								hidden={tab.hidden}
								className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-transparent data-[state=active]:text-primary! bg-transparent! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2"
							>
								{tab.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>

			<div className="flex-1 overflow-auto bg-background text-foreground p-4">
				{activeTab === 'overview' && (
					<div className="space-y-6 animate-in fade-in slide-in-from-left-2 duration-300 pb-12">
						<DamageForm
							damage={damage}
							onSubmit={handleFormSubmit}
							onDelete={handleDelete}
							onCancel={onBack}
							isSubmitting={isSubmitting}
							calculateDamageSize={calculateDamageSize}
							restoreButton={restoreButton}
						/>
						{damage?.id && (
							<DeleteConfirmDialog
								open={isDeleteOpen}
								onOpenChange={setIsDeleteOpen}
								target={softDeleteTargets.damage(damage.id)}
								name={damageName}
								isDeleting={softDeleteDamage.isPending}
								onConfirm={({ acceptWorkPackageImpact }) =>
									softDeleteDamage.mutate({
										target: softDeleteTargets.damage(damage.id as string),
										name: damageName,
										acceptWorkPackageImpact,
										successMessage: t('damages.toast.deleteSuccess'),
									})
								}
							/>
						)}
						<Restricted to={[ROLE.DAMAGES_UPDATE, ROLE.DAMAGES_CREATE]}>
							{!isNew && (
								<div className="flex items-center gap-2 mb-4">
									<Button variant="outline" size="sm" onClick={handleDuplicate}>
										<Copy className="mr-2 h-3.5 w-3.5" /> {t('damages.detail.duplicate')}
									</Button>

									<Button variant="outline" size="sm" onClick={() => setIsMoveDialogOpen(true)}>
										<ArrowRightLeft className="mr-2 h-3.5 w-3.5" /> {t('common.actions.move')}
									</Button>
								</div>
							)}
						</Restricted>
					</div>
				)}
				{!deletedBanner && activeTab === 'data-upload' && (
					<DataUploadListContainer
						type="damage"
						assetId={assetId}
						damageId={damage?.id}
						inspectionId={inspectionId}
					/>
				)}
			</div>

			{damage && !isNew && (
				<MoveDamageDialog
					isOpen={isMoveDialogOpen}
					onClose={() => setIsMoveDialogOpen(false)}
					assetId={assetId}
					currentInspectionId={inspectionId}
					damageId={damage.id!}
					damageTitle={title || t('damages.detail.thisDamage')}
					polygons={damage.polygons}
					onMoveSuccess={() => {
						mapActions({ type: 'cancel', payload: {} });
					}}
				/>
			)}
		</>
	);
}

const ActionIcon = ({ icon: Icon, onClick, title }: any) => (
	<button
		onClick={onClick}
		type="button"
		className="text-muted-foreground hover:text-primary p-0.5 hover:bg-muted rounded-sm transition-colors"
		title={title}
	>
		<Icon className="h-3 w-3" />
	</button>
);
